import React, { useContext, useEffect, useRef } from "react";
import { connect } from "react-redux";
import Stopwatch from "components/Shared/StopWatch";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import CaseAlerts from "components/CaseAlerts/CaseAlerts";
import Select from "react-select";
import { classes } from "components/Utility";
import ReactSelectClassName from "components/Shared/MSelect";
import {
  setQAReady,
  setQAStarted,
  setQAPaused,
  setQAValidationErrors,
  setQAKeepGettingCases,
  setQAShowRejectDialog,
  setQAShowSendManagerDialog,
  setQAShowCompletedCaseDialog,
  getNextCase,
  resetQAState,
  setQATimeSpent,
  getCaseForms,
  getCaseSketch,
  getCaseAlerts,
  resetQACase,
  setQATimerInfo,
} from "actions/qualityAssurance";
import { QUALITY_REVIEW_ASSIGN, CLOCK_IN_OR_OUT_FUNCTION, QA_CASE_AUDIT_EDIT, CASE_FORMS_MAINTENANCE } from "typeConstants/rights";
import { appraisalWorkflowStatusTypeEnum } from "typeConstants/appraisalWorkflowStatusType";
import ManageFormsDialog from "./ManageFormsDialog";
import { manageFormsRedux } from "requests/homeowner/ho";
import { EmployeeTimeClockDto, GetCasePhotosRequest, GetCasePhotosResponse, GetCaseSketchRequest, GetCaseSketchResponse, GetNextCaseResponse, QueueTypeEnum } from "interfaces/Mueller.Business.Models.ts";
// renamed so no one accidentally calls start timer when they should be calling the function on the class to stop the timer
import { startTimer as startTimerApi, getActiveTimer } from "requests/caseUserTimer/index.ts";
import { DialogContext } from "components/Shared/DialogProvider.tsx";
import { withPhoenixForms } from "components/PhoenixFormsV4/index.tsx";
import { PhoenixFormsProviderValue } from "components/PhoenixFormsV4/PhoenixFormsProvider.tsx";
import { userHasRight } from "selectors/rights";

interface Props {
  history: any,
  phoenixFormsContext: PhoenixFormsProviderValue,
  setQAReady: (isQAReady: boolean) => void,
  setQAStarted: (isQAStarted: boolean) => void,
  setQAPaused: (isQAPaused: boolean) => void,
  setQAValidationErrors: (qaValidationErrors: any) => void,
  validationErrors: any, //Having some difficulties finding what the definition would be for this
  summaryViewEnabled: boolean,
  canToggleSummaryView: boolean,
  setQAKeepGettingCases: (shouldQAKeepGettingCases: boolean) => void,
  keepGettingCases: boolean,
  setQAShowRejectDialog: (shouldShowRejectDialog: boolean) => void,
  setQAShowSendManagerDialog: (shouldShowManagerDialog: boolean) => void,
  setQAShowCompletedCaseDialog: (shouldShowCompletedCaseDialog: boolean) => void,
  setQATimeSpent: (qaTimeSpent: number) => void,
  qaCase: GetNextCaseResponse,
  getNextCase: (payload: {ControlNumber: number}, callback: (response: GetNextCaseResponse) => void) => void,
  getCaseForms: (id: number) => void,
  getCasePhotos: (payload: GetCasePhotosRequest, callback: (response: GetCasePhotosResponse) => void) => void,
  getCaseSketch: (payload: GetCaseSketchRequest, callback: GetCaseSketchResponse) => void,
  getCaseAlerts: () => void,
  qaSaveDataFunction: () => void,
  resetQAState: () => void,
  completingCase: boolean,
  rejectingCase: boolean,
  escalatingCase: boolean,
  requestSuccessful: boolean,
  activeClock: EmployeeTimeClockDto,
  rights: string[],
  controlNumber: number,
  beginManageForms: (payload: boolean) => void,
  resetQACase: (callBack: () => void) => void,
  setQATimerInfo: (timerId: number, timerPingIntervalSeconds: number) => void,
  secondaryStatus: string,
  appraisalWorkflowStatusType: number,
  updatingCaseIssues: boolean,
  caseAuditRequired: boolean,
  caseAuditCompleted: boolean,
  caseAuditFeedbackCompleted: boolean,
  qaPaused: boolean,
  qaReady: boolean,
  canEditCaseForms: boolean,
}

const QAHeader = (props: Props) => {
	
  const dialog = useContext(DialogContext);
  const mounted = useRef(true);

  useEffect(() => {

    const { location } = props.history;
    if (location.state && location.state.controlNumber)
      startQA();

    return () => {
      mounted.current = false;
      props.resetQAState();
    };
  }, []);

  const openRegularCaseView = (controlNumber) => {
    props.history.push({
      pathname: "/case",
      state: { controlNumber: controlNumber }
    });
  };

  const startQA = () => {
    const { setQAStarted } = props;
    setQAStarted(true);
    const {
      setQAReady,
      setQAPaused,
      setQAValidationErrors,
      getNextCase,
      history: { location: { state } },
      setQATimeSpent,
      resetQACase,
      setQATimerInfo,
    } = props;
    setQAReady(false);
    setQAPaused(true);
    setQAValidationErrors([]);
    setQATimeSpent(0.0);

    getActiveTimer(mounted, controlNumber => {
      if (controlNumber) {
        if (state?.controlNumber) {
          // opening a case from search
          openRegularCaseView(state?.controlNumber);
        } else {
          // starting qa on a case
          dialog.showOk(
            "You already have a case open",
            `Control #${controlNumber} is open in another window, or you have recently closed the window that had that case open. If you have recently closed the case, wait a minute and try again to reopen this case`,
            () => openRegularCaseView(controlNumber)
          );
        }
      } else {
        resetQACase(() => getNextCase(
          { ControlNumber: state?.controlNumber },
          (resp) => startTimer(resp.controlIdNumber, response => setQATimerInfo(response.id, response.pingRateSeconds)))
        );
      }
    });
  };

  // callback is called with type StartTimerResult passed as the param
  const startTimer = (controlNumber, callback) => {
    startTimerApi(controlNumber, mounted, callback);
  };

  const reloadCase = () => {
    const { setQAReady, setQAPaused, getNextCase, controlNumber } = props;
    setQAReady(false);
    setQAPaused(true);
    getNextCase({ ControlNumber: controlNumber }, () => {
      setQAPaused(false);
      setQAReady(true);
    });
  };

  const showRejectDialog = () => {
    if (props.qaCase.daysTillCustomerDue  && props.qaCase.daysTillCustomerDue > 0 && props.qaCase.daysTillCustomerDue < 4) {
      dialog.showYesNo("Rejection Warning", `Case Due Date is ${props.qaCase.daysTillCustomerDue} ${props.qaCase.daysTillCustomerDue === 1 ? "day" : "days"} away. Are you sure you want to reject this case back to the FR?`, () => props.setQAShowRejectDialog(true));
    }
    else if (props.qaCase.daysTillCustomerDue && props.qaCase.daysTillCustomerDue < 0) {
      dialog.showYesNo("Rejection Warning", "Case is Overdue. Are you sure you want to reject this case back to the FR?", () => props.setQAShowRejectDialog(true));
    }
    else if (props.qaCase.daysTillCustomerDue !== null && props.qaCase.daysTillCustomerDue === 0) {
      dialog.showYesNo("Rejection Warning", "Case Due Date is today. Are you sure you want to reject this case back to the FR?", () => props.setQAShowRejectDialog(true));
    }
    else {
      props.setQAShowRejectDialog(true);
    }
  };

  const showSendManagerDialog = () => {
    props.setQAShowSendManagerDialog(true);
  };

  const showCompletedCaseDialog = () => {
    props.setQAShowCompletedCaseDialog(true);
  };

  const handleCaseActionDropdownChange = (selected) => {
    selected.value();
  };

  const mapStatusToButtonDefs = () => {
    const {
      qaCase: { qaQueueTypeId },
      qaSaveDataFunction,
      rights,
      secondaryStatus,
      appraisalWorkflowStatusType,
      updatingCaseIssues,
      caseAuditRequired,
    } = props;

    const buttonDefs = {
      Save: {
        label: "Save",
        value: qaSaveDataFunction,
      },
      returnCase: {
        label: qaQueueTypeId === QueueTypeEnum.AppraisalQa ? "Return To Appraiser" : "Return To Field",
        value: showRejectDialog,
      },
      sendToManager: {
        label: rights.includes(QUALITY_REVIEW_ASSIGN) ? "Send Case" : "Send To Manager",
        value: showSendManagerDialog,
      },
      completeCase: {
        label: "Complete Case",
        value: showCompletedCaseDialog,
      },
      auditRequired: {
        label: "AUDIT REQUIRED",
        value: null,
      },      
    };
    
    if (caseAuditRequired && qaQueueTypeId === QueueTypeEnum.AuditQa && rights.includes(QA_CASE_AUDIT_EDIT)) {      
      return [
        buttonDefs.Save,
        buttonDefs.returnCase,        
        buttonDefs.completeCase,
        buttonDefs.auditRequired,        
      ];
    } else if (!updatingCaseIssues && secondaryStatus === "QA" 
      || appraisalWorkflowStatusType === appraisalWorkflowStatusTypeEnum.IN_APPRAISAL_QA 
      || appraisalWorkflowStatusType === appraisalWorkflowStatusTypeEnum.ASSIGNED_TO_QA_MANAGER) {
      return [
        buttonDefs.Save,
        buttonDefs.returnCase,
        buttonDefs.sendToManager,
        buttonDefs.completeCase
      ];
    } else {
      return [
        buttonDefs.Save,
        buttonDefs.sendToManager,
      ];
    }
  };

  const isRenderingCaseFormsButton = props.summaryViewEnabled && props.qaReady && props.canEditCaseForms;

  return (
    <div className="qa-portal-top-wrapper">
      <div className="summary-view-toggle" style={props.canToggleSummaryView && props.qaReady ? { visibility: "visible" } : { visibility: "hidden" }}>
        {/* We need to be able to enable Dialog in state using this button. This enables the dialog to show in global state, but it's rendered in MCaseView */}
        {isRenderingCaseFormsButton && <div>
          <Tooltip title={props.phoenixFormsContext.needsSave ? "Unsaved Form Changes!" : ""}>
            <span>
              <Button
                disabled={props.qaPaused || props.phoenixFormsContext.needsSave}
                onClick={() => props.beginManageForms(true)}
              >Manage Forms</Button>
            </span>
          </Tooltip>
        </div>}
      </div>
      <div
        className="days-till-due-timer"
        style={{
          ...(props.qaReady ? { visibility: "visible" } : { visibility: "hidden" }),
          marginLeft: "auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center"
        }}
      >
        <div className="timer-label">{"Days Till Due"}</div>
        <div style={{ fontSize: '16px', fontWeight: 500, textAlign: 'center' }}>
          {props.qaCase.daysTillCustomerDue}
        </div>
      </div>
      <div
        className="case-timer"
        style={{
          ...(props.qaReady ? { visibility: "visible" } : { visibility: "hidden" }),
          display: "flex",
          flexDirection: "column",
          justifyContent: "center"
        }}
      >
        <div className="timer-label">{"Time on Case"}</div>
        <Stopwatch
          key={`${props.qaReady}`}
          hideStopStart
          tickOnMount={props.qaReady}
          whiteFont
          onChange={secondsElapsed => {
            props.setQATimeSpent(secondsElapsed);
          }}
          paused={false}
        />
      </div>
      <div
        className="get-next-case-toggle"
        style={props.qaReady ? { visibility: "visible" } : { visibility: "hidden" }}
      >
        <FormControl component="fieldset">
          <FormGroup aria-label="position" name="position" row>
            <FormControlLabel
              style={{ fontSize: "12px" }}
              value="top"
              control={
                <Checkbox
                  color="default"
                  checked={props.keepGettingCases}
                  value={props.keepGettingCases}
                  onChange={() => props.setQAKeepGettingCases(!props.keepGettingCases)}
                  sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                />}
              label={<span style={{ color: 'white' }}>Keep Getting Cases</span>}
              labelPlacement="top"
            />
          </FormGroup>
        </FormControl>
      </div>
      <div
        style={{
          ...(props.qaReady ? { visibility: "visible" } : { visibility: "hidden" }),
          paddingRight: 10,
          display: "flex",
          alignItems: "center"
        }}
      >
        <CaseAlerts />
      </div>
      <div className="qa-case-options-buttons">
        <div
          className="case-action-buttons"
          style={
            props.qaReady ? { visibility: "visible" } : { visibility: "hidden" }
          }
        >
          {mapStatusToButtonDefs().map(({ label, value }) =>
            <Button
              key={label}
              disabled={props.qaPaused || !value}
              onClick={value}
            >{label}</Button>
          )}
        </div>
        <div
          className="case-action-dropdown"
          style={
            props.qaReady ? { visibility: "visible" } : { visibility: "hidden" }
          }
        >
          <Select
            placeholder="Case Actions"
            className={classes("react-select-button-dropdown", ReactSelectClassName)}
            classNamePrefix="react-select"
            isSearchable={false}
            value="Case Actions"
            options={mapStatusToButtonDefs()}
            onChange={handleCaseActionDropdownChange}
          />
        </div>
        <div
          className="start-qa-button"
          style={
            props.qaReady
              ? { visibility: "hidden", display: "none" }
              : { visibility: "visible" }
          }
        >
          <Button
            className="item"
            disabled={props.qaPaused || (!(props.activeClock && props.activeClock.clockInDate) && props.rights.includes(CLOCK_IN_OR_OUT_FUNCTION))}
            onClick={startQA}
          >
            Start QA
          </Button>
        </div>
      </div>
    </div>
  );
  
};

const mapStateToProps = state => {
  return {
    qaReady: state.qualityAssurance.qaVariables.qaReady,
    qaPaused: state.qualityAssurance.qaVariables.qaPaused,
    validationErrors: state.qualityAssurance.qaVariables.validationErrors,
    summaryViewEnabled: state.qualityAssurance.qaVariables.summaryViewEnabled,
    canToggleSummaryView: state.qualityAssurance.qaVariables.canToggleSummaryView,
    keepGettingCases: state.qualityAssurance.qaVariables.keepGettingCases,
    qaCase: state.qualityAssurance.qaCase,
    qaSaveDataFunction: state.qualityAssurance.qaVariables.qaSaveDataFunction,
    completingCase: state.qualityAssurance.qaVariables.completingCase,
    rejectingCase: state.qualityAssurance.qaVariables.rejectingCase,
    escalatingCase: state.qualityAssurance.qaVariables.escalatingCase,
    requestSuccessful: state.qualityAssurance.requestSuccessful,
    activeClock: state.user.activeClock,
    rights: state.user.profile.rights,
    controlNumber: state.qualityAssurance.qaCase?.controlIdNumber,
    secondaryStatus: state.qualityAssurance.qaCase?.caseInfo?.secondaryStatus,
    appraisalWorkflowStatusType: state.qualityAssurance.qaCase?.caseInfo?.appraisalWorkflowStatusType,
    updatingCaseIssues: state.qualityAssurance.updatingCaseIssues,
    caseAuditRequired: state.qualityAssurance.qaCase?.caseAuditRequired,
    caseAuditCompleted: state.qualityAssurance.qaCase?.caseAuditCompleted,
    caseAuditFeedbackCompleted: state.qualityAssurance.qaCase?.caseAuditFeedbackCompleted,
    canEditCaseForms: userHasRight(CASE_FORMS_MAINTENANCE)(state),
  };
};

const mapDispatchToProps = dispatch => ({
  setQAReady(isQAReady) {
    dispatch(setQAReady(isQAReady));
  },
  setQAStarted(isQAStarted) {
    dispatch(setQAStarted(isQAStarted));
  },
  setQAPaused(isQAPaused) {
    dispatch(setQAPaused(isQAPaused));
  },
  setQAValidationErrors(qaValidationErrors) {
    dispatch(setQAValidationErrors(qaValidationErrors));
  },
  setQAShowRejectDialog(shouldShowRejectDialog) {
    dispatch(setQAShowRejectDialog(shouldShowRejectDialog));
  },
  setQAShowSendManagerDialog(shouldShowManagerDialog) {
    dispatch(setQAShowSendManagerDialog(shouldShowManagerDialog));
  },
  setQAShowCompletedCaseDialog(shouldShowCompletedCaseDialog) {
    dispatch(setQAShowCompletedCaseDialog(shouldShowCompletedCaseDialog));
  },
  setQAKeepGettingCases(shouldQAKeepGettingCases) {
    dispatch(setQAKeepGettingCases(shouldQAKeepGettingCases));
  },
  getNextCase(payload, callback) {
    dispatch(getNextCase(payload, callback));
  },
  resetQAState() {
    dispatch(resetQAState());
  },
  setQATimeSpent(qaTimeSpent) {
    dispatch(setQATimeSpent(qaTimeSpent));
  },
  getCaseForms(id) {
    dispatch(getCaseForms(id));
  },
  getCaseSketch(payload, callback) {
    dispatch(getCaseSketch(payload, callback));
  },
  getCaseAlerts() {
    dispatch(getCaseAlerts());
  },
  beginManageForms(payload) {
    dispatch(manageFormsRedux(payload));
  },
  resetQACase(callBack = null) {
    dispatch(resetQACase(callBack));
  },
  setQATimerInfo(timerId, timerPingIntervalSeconds) {
    dispatch(setQATimerInfo(timerId, timerPingIntervalSeconds));
  },
});

export default withPhoenixForms(connect(
  mapStateToProps,
  mapDispatchToProps
)(QAHeader));
